// Layout entry point - Global scripts that run on every page

// Namespace import + a real assignment, NOT a bare `import 'bootstrap'`. The bare form is
// side-effect-only, so webpack tree-shook it and Bootstrap's click data-API was never
// registered: the Schools dropdown silently refused to open (aria-expanded stayed false,
// no console error), and the `typeof bootstrap !== 'undefined'` guard below never passed,
// so tooltips/popovers were dead too. Collapse still worked, which is why the mobile
// hamburger masked this. Importing the namespace forces every component module to evaluate.
import * as bootstrap from 'bootstrap';
window.bootstrap = bootstrap;

// Solid only, not all.min.css. Every icon in this codebase is fa-solid (11 distinct ones);
// all.min.css additionally ships the brands and regular families - hundreds of unused glyph
// rules plus their @font-face declarations - on every page, and it is render-blocking.
import '@fortawesome/fontawesome-free/css/fontawesome.min.css';
import '@fortawesome/fontawesome-free/css/solid.min.css';
import { moduleEnabled, trackEvent } from './analytics.js';
import { isOutbound, linkKind } from './analytics-buckets.js';
// Self-initialising; no-ops when the banner partial is not on the page.
import './consent/banner.js';
// Self-initialising; drains the one-shot signal cookie the 302 auth paths leave behind.
import './analytics-signal.js';

/**
 * Runs one enhancement in isolation.
 *
 * Every enhancement below used to share a single un-guarded DOMContentLoaded callback, so one throw
 * anywhere would silently kill everything registered after it - the Explore menu's outside-click
 * close, Escape-to-close, the mobile TOC collapse, and the desktop TOC keyboard guard - on all
 * ~2,300 pages, with the only symptom being "the nav feels broken". Enhancements are independent by
 * definition; failing independently is the behaviour that matches.
 */
function enhancement(name, fn) {
    try {
        fn();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MbaLab] enhancement "' + name + '" failed:', error);
    }
}

/**
 * Runs the whole initialiser at most once per page, however many bundles contain it.
 *
 * webpack.config.js sets `default: false` (app source must never be auto-split, because the Razor
 * views load bundles by name), so every entry that does `import './layout.js'` - home, index -
 * carries its own complete copy. _Layout.cshtml ALSO loads layout.bundle.js on every page, so on
 * `/` this file is evaluated twice and its DOMContentLoaded handler fires twice.
 *
 * That was harmless when the handler only wired up nav and TOC behaviour. It stopped being harmless
 * the moment it started attaching delegated analytics listeners on `document`: two listeners means
 * one click sends two outbound_click or cta_click events, silently, forever. A window flag rather
 * than a module-scoped one on purpose - the whole problem is that there are two module instances,
 * each with its own scope.
 */
if (window.__mbalabLayoutInit) {
    // eslint-disable-next-line no-console
    console.debug('[MbaLab] layout.js evaluated twice; skipping duplicate init.');
} else {
    window.__mbalabLayoutInit = true;

// Initialize global functionality
document.addEventListener('DOMContentLoaded', function() {
    // NOTE: do not add a manual click handler for [data-bs-toggle="dropdown"]. The namespace
    // import above is what makes Bootstrap's own click data-API work; adding an explicit
    // handler on top of it toggles the menu twice per click (open then immediately closed),
    // which looks exactly like a dropdown that never opens.

    // Initialize tooltips and popovers if Bootstrap is available
    enhancement('bootstrap-tooltips-popovers', function () {
        if (typeof bootstrap === 'undefined') return;

        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new bootstrap.Tooltip(tooltipTriggerEl);
        });

        const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
        popoverTriggerList.map(function (popoverTriggerEl) {
            return new bootstrap.Popover(popoverTriggerEl);
        });
    });

    // The header's Explore menu is a native <details> (see _SiteHeader.cshtml), which
    // stays open until its summary is clicked again. Close it on outside click/Escape as
    // progressive enhancement - this never toggles the summary itself, so it cannot
    // reintroduce the double-toggle failure documented above.
    enhancement('nav-dropdown-dismiss', function () {
        document.addEventListener('click', function (e) {
            document.querySelectorAll('details.nav-dropdown[open]').forEach(function (menu) {
                if (!menu.contains(e.target)) {
                    menu.removeAttribute('open');
                }
            });
        });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                document.querySelectorAll('details.nav-dropdown[open]').forEach(function (menu) {
                    menu.removeAttribute('open');
                });
            }
        });
    });

    // Article TOC (.toc-card): open by default so it works with no JS at
    // all; on phones/tablets, start it collapsed instead. Read once on
    // load, not a resize listener - matches this file's other enhancements.
    enhancement('toc-collapse-mobile', function () {
        if (window.matchMedia('(max-width: 991.98px)').matches) {
            document.querySelectorAll('[data-collapse-mobile]').forEach(function (el) {
                el.removeAttribute('open');
            });
        }
    });

    // Outbound clicks, delegated once rather than bound per link. Seven views render these - the
    // school's own site, the Net Price Calculator, College Scorecard, the AACSB listing, program
    // pages, and leadership claim sources - and binding each would mean editing every one of them
    // and remembering to edit the next.
    //
    // moduleEnabled is checked BEFORE attaching, not inside the handler: that is the difference
    // between a kill switch that stops data and one that stops behaviour.
    enhancement('analytics-outbound', function () {
        if (!moduleEnabled('outboundLinks')) return;

        document.addEventListener('click', function (event) {
            const link = event.target.closest && event.target.closest('a[href]');
            if (!link) return;

            let url;
            try {
                url = new URL(link.href, window.location.href);
            } catch (error) {
                return; // malformed or unresolvable href
            }

            // mailto:, tel: and javascript: are not outbound navigations.
            if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
            if (!isOutbound(url.hostname, window.location.hostname)) return;

            trackEvent('outbound_click', {
                // An explicit data-link-kind wins over host sniffing - a school's Net Price
                // Calculator usually lives on the school's own domain, so the host alone would file
                // the highest-intent click on the page as a generic school-site visit.
                link_kind: linkKind(url.hostname, link.getAttribute('data-link-kind')),
                link_domain: url.hostname,
            });
        // Capture phase: these links carry target="_blank", and capturing means a handler further
        // down that stops propagation cannot silently cost us the event.
        }, true);
    });

    // Internal conversion links, marked with data-cta in the shared partials. Delegated for the same
    // reason as above - and because the alternative, matching on href, would break the moment a
    // route changed.
    enhancement('analytics-cta', function () {
        if (!moduleEnabled('cta')) return;

        document.addEventListener('click', function (event) {
            const el = event.target.closest && event.target.closest('[data-cta]');
            if (!el) return;

            const params = { cta_id: el.getAttribute('data-cta') };
            const placement = el.getAttribute('data-cta-placement');
            if (placement) params.cta_placement = placement;

            trackEvent('cta_click', params);
        }, true);
    });

    // Desktop's TOC is meant to be a fixed rail, not collapsible - the CSS
    // already sets pointer-events:none on its summary, but that only stops
    // mouse clicks. A focused <summary> still natively toggles on Enter/
    // Space regardless of pointer-events (it's not a pointer interaction),
    // so a keyboard user could silently collapse it with no visible way to
    // tell why (the chevron is display:none at this width too). Checked
    // live, not once at load, since this can't regress on resize the way
    // the mobile-collapse-on-load enhancement above can.
    enhancement('toc-desktop-keyboard-guard', function () {
        document.querySelectorAll('.toc-card > summary').forEach(function (summary) {
            summary.addEventListener('click', function (e) {
                if (window.matchMedia('(min-width: 992px)').matches) {
                    e.preventDefault();
                }
            });
        });
    });
});

}
